import { formatMoney } from '@/lib/format';

/** Human-readable label for an IGM action code (ONDC issue.actions[].descriptor.code). */
export function igmActionLabel(code?: string): string {
  if (!code) return 'Update';
  const key = code.toUpperCase();
  return (
    {
      OPEN: 'Complaint opened',
      PROCESSING: 'Operator is reviewing',
      INFO_REQUESTED: 'More information requested',
      INFO_PROVIDED: 'Information provided',
      RESOLUTION_PROPOSED: 'Resolution offered',
      RESOLUTION_ACCEPTED: 'Resolution accepted',
      RESOLUTION_REJECTED: 'Resolution rejected',
      ESCALATED: 'Escalated to grievance',
      CLOSED: 'Issue closed',
    }[key] ?? code.replaceAll('_', ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
  );
}

/** Human-readable label for a seller resolution action (REFUND, REPLACEMENT, …). */
export function igmResolutionActionLabel(code?: string): string {
  if (!code) return 'Resolution';
  const key = code.toUpperCase();
  return (
    {
      REFUND: 'Refund',
      CANCEL: 'Cancellation',
      'NO-ACTION': 'No action needed',
      NO_ACTION: 'No action needed',
      REPLACEMENT: 'Replacement',
      PARENT: 'Options',
    }[key] ?? code.replaceAll('_', ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
  );
}

/** Format ONDC refund_amount or RESOLUTION_DETAILS/REFUND_AMOUNT tag value for display. */
export function formatIgmRefundAmount(raw?: string): string {
  if (!raw?.trim()) return '';
  const trimmed = raw.trim();
  if (trimmed.includes('.')) {
    return formatMoney({ value: trimmed, currency: 'INR' });
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return formatMoney({ value: trimmed, currency: 'INR' });
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(n);
}

/** True for ONDC Workbench / cert-mock provider strings — never show these to riders. */
export function isCertIgmProvider(raw?: string): boolean {
  if (!raw?.trim()) return false;
  const id = raw.toLowerCase();
  if (/(^|[./])workbench|pramaan|automation|staging-automation|\/mock/.test(id)) {
    return true;
  }
  return /^NP[\dA-Z-]*$/i.test(raw.trim());
}

/** Trim a real operator org name for display; returns '' for cert-mock / fixture actors. */
export function igmProviderLabel(raw?: string): string {
  if (!raw?.trim() || isCertIgmProvider(raw)) return '';
  const base = raw.split('::')[0]?.split('.')[0] ?? raw;
  if (isCertIgmProvider(base)) return '';
  return base.charAt(0).toUpperCase() + base.slice(1);
}
